<script>
import InputComment from '@/components-v4/input-comment.vue'
import BaseButton from '@/components-v4/button.vue'
import { TRACKING_EVENTS } from '@/constants/tracking'
import LavenderMixin from '@/mixins/lavender'

const STEPS = {
  select_answer: 'select_answer',
  input_comment: 'input_comment',
}
export default {
  name: 'QuestionnaireContent',
  components: { BaseButton, InputComment },
  mixins: [LavenderMixin],
  props: {
    comment: Proptypes.string(''),
    direction: Proptypes.string('ltr'),
    question: Proptypes.string(''),
    questionDescription: Proptypes.string(''),
    answers: Proptypes.array(),
    fetchingAnswer: Proptypes.boolean(false),
    selectedAnswerId: Proptypes.any([Number, String])(),
    disabledConfirmButton: Proptypes.boolean(),
    merchantName: Proptypes.string(''),
    showContinueWithTamaraButton: Proptypes.boolean(true),
    showCloseButton: Proptypes.boolean(true),
  },
  data() {
    return {
      currentStep: STEPS.select_answer,
    }
  },
  computed: {
    commentValue: {
      get() {
        return this.comment
      },
      set(value) {
        this.$emit('update:comment', value)
      },
    },
    isSelectAnswerStep() {
      return this.currentStep === STEPS.select_answer
    },
    arrowIcon() {
      return this.$options.filters.getIconNameByDirection(this.direction, {
        ltr: 'arrow-left',
        rtl: 'arrow-right',
      })
    },
    selectedAnswerText() {
      const foundAnswer = this.answers.find(
        (answer) => answer.answerId === this.selectedAnswerId
      )

      if (!foundAnswer) {
        return ''
      }

      return foundAnswer.answer || ''
    },
  },
  methods: {
    onChangedAnswer(value) {
      if (this.value !== this.selectedAnswerId) {
        this.$emit('update:selectedAnswerId', value)
        this.trackEvent(
          TRACKING_EVENTS.CANCEL_CANCELLATION_REASON_RADIO_CLICKED.EVENT_NAME,
          {
            feedback_answer_id: value,
          }
        )
        this.changeToStep(STEPS.input_comment)
      }
    },
    changeToStep(step) {
      if (!Object.keys(STEPS).includes(step)) {
        throw new Error(`Step ${step} is not supported yet`)
      }

      this.currentStep = step
    },
    handleBackToAnswerList() {
      this.changeToStep(STEPS.select_answer)
    },
    onCancellationTextFocus() {
      this.trackEvent(
        TRACKING_EVENTS.CANCEL_CANCELLATION_REASON_INPUT_INTERACTED.EVENT_NAME
      )
    },
    onCancel() {
      this.resetData()

      this.$emit('cancel')
    },
    onConfirm() {
      this.resetData()

      this.$emit('confirm')
    },
    resetData() {
      this.changeToStep(STEPS.select_answer)
    },
  },
}
</script>

<template>
  <div :class="isLavenderReskinEnabled ? 'questionnaire-content-lavender' : ''">
    <div class="mt-1">
      <v-skeleton-loader
        v-if="fetchingAnswer"
        max-width="100%"
        type="list-item"
      />
      <div v-else>
        <div class="text-title-4 text-start tamara-title-1--sm">
          <div>{{ question }}</div>
          <v-btn
            v-if="showCloseButton && isLavenderReskinEnabled"
            fab
            small
            class="flex-grow-0 flex-shrink-0 close-btn"
            color="white"
            @click="onCancel"
          >
            <SvgIcon size="20px" color="#000" name="close" />
          </v-btn>
        </div>

        <div class="mt-3 question-description">
          {{ questionDescription }}
        </div>
      </div>
    </div>
    <div v-if="fetchingAnswer">
      <v-skeleton-loader max-width="100%" type="list-item-three-line" />
      <v-skeleton-loader max-width="100%" type="list-item-three-line" />
    </div>
    <v-radio-group
      v-else-if="isSelectAnswerStep"
      :value="selectedAnswerId"
      column
      class="user-feedback-dialog-radio-group"
      @change="onChangedAnswer"
    >
      <v-radio
        v-for="answer in answers"
        :key="answer.answerId"
        :label="answer.answer"
        :value="answer.answerId"
        :off-icon="
          isLavenderReskinEnabled ? '$lavenderRadioOff' : '$tamaraRadioOff'
        "
        :on-icon="
          isLavenderReskinEnabled ? '$lavenderRadioOn' : '$tamaraRadioOn'
        "
        class="survey-radio"
      />
    </v-radio-group>
    <div v-else class="mt-5">
      <div
        v-ripple
        class="w-full d-flex align-center justify-start back-to-answer-list"
        @click="handleBackToAnswerList"
      >
        <SvgIcon size="20px" color="gray" :name="arrowIcon" />
        <span class="ms-2 text-title-3 selected-answer-text">
          {{ selectedAnswerText }}
        </span>
      </div>
      <InputComment
        v-model="commentValue"
        :max-len-of-comment="500"
        :placeholder="$t('cancel_session_user_feedback_comment_placeholder')"
        class="mt-5"
        @focus="onCancellationTextFocus"
      />
    </div>
    <div class="w-full d-flex flex-column mt-7">
      <BaseButton
        class="confirm-cancel"
        :disabled="disabledConfirmButton"
        @click="onConfirm"
      >
        {{ $t('cancel_session_button_confirm_feedback', { merchantName }) }}
      </BaseButton>
      <BaseButton
        v-if="showContinueWithTamaraButton"
        outlined
        class="mt-3 cancel-button-dialog__confirm-btn"
        @click="onCancel"
      >
        {{ $t('cancel_session_button_cancel_feedback') }}
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.user-feedback-dialog-radio-group {
  .v-input__slot {
    margin-bottom: 0 !important;
  }

  .v-messages {
    display: none;
  }

  .survey-radio {
    label {
      text-align: initial;
    }
  }
}
.questionnaire-content-lavender {
  font-family: 'Plus Jakarta Sans', sans-serif;
  .confirm-cancel {
    background-color: #200044 !important;
    border-radius: 32px !important;
    ::v-deep(.v-btn__content) {
      color: #fff;
      font-size: 14px;
      font-weight: 600 !important;
      line-height: 24px;
      padding: 12px 16px !important;
    }
    &:disabled {
      ::v-deep(.v-btn__content) {
        color: #7e82ad !important;
        font-weight: 500 !important;
        line-height: 23px !important;
      }
    }
  }
  .selected-answer-text {
    color: #16181d !important;
  }

  .back-to-answer-list {
    .tamara-svg-icon {
      background-color: #f4f6f9 !important;
      width: 32px !important;
      height: 32px !important;
      border-radius: 100px !important;
      padding: 8px !important;
    }
  }

  .question-description {
    color: #585b7a !important;
    opacity: 1 !important;
  }

  ::v-deep(.tamara-base-comment-input) {
    textarea::placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
    textarea::-webkit-input-placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
    textarea::-moz-placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
    textarea:-ms-input-placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
  }

  // Alternative: Target the theme classes
  ::v-deep(.theme--light .tamara-base-comment-input) {
    textarea::placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
    textarea::-webkit-input-placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
    textarea::-moz-placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
    textarea:-ms-input-placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
  }

  .d-flex.gray--text {
    color: #7e82ad !important;
  }

  ::v-deep(.survey-radio) {
    .v-label {
      color: #585b7a !important;
    }
  }

  // Most aggressive approach - target any textarea with placeholder
  ::v-deep(.tamara-base-comment-input textarea[placeholder]) {
    &::placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
    &::-webkit-input-placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
    &::-moz-placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
    &:-ms-input-placeholder {
      color: #7e82ad !important;
      opacity: 1 !important;
    }
  }

  .text-title-4 {
    position: relative;
    text-align: center !important;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 24px;
    div {
      width: 60%;
      text-align: center !important;
      font-size: 18px;
      font-weight: 600;
      color: #16181d;
      padding: 0;
      margin-top: 0;
      position: relative;
    }
    width: 100%;
    .close-btn {
      background-color: #f4f6f9 !important;
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
    }
  }
  .v-radio-group {
    .v-radio {
      .v-input__slot {
        margin-bottom: 0 !important;
      }
    }
  }
}
</style>
